/**
 * Lunchbox Web Ordering's Dynamic Factory v1
 * The Dynamic Factory is responsible for parsing structure and render data
 *
 * It is recommended to import this component as a default export for best performance.
 * Use named export for the default structure or typescript definitions.
 *
 * Here is a quick example:
 * import DynamicFactory, { DynamicStructure, View, Label, Button, ...etc } from "@lunchboxinc/dynamic-web/DynamicFactory";
 */
import * as React from "react";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useHover, usePress } from "@react-aria/interactions";
import { mergeProps } from "@react-aria/utils";
import chroma from "chroma-js";
import merge from "../helpers/merge";
import { prefixWith$ } from "./helpers";
import {
  ButtonType,
  CollectionViewType,
  DynamicFactoryProps,
  DynamicNodeType,
  DynamicStructureType,
  InputType,
  LabelType,
  LinearLayoutType,
  NodeState,
  StatefulNode,
  StructureContextValue,
  TextStyles,
  Styles,
  ViewType,
} from "./types";
import {
  ButtonComponent,
  ButtonComponentProps,
  CollectionViewComponent,
  InputComponent,
  InputComponentProps,
  LabelComponent,
  LabelComponentProps,
  ViewComponentProps,
  ViewComponent,
  LinearLayoutComponent,
} from "./styled";
import {
  StructureContext,
  useStructureContext,
  NodeStateContext,
} from "./context";
import defaultStructure from "./structure";

const isDevelopment = process.env.NODE_ENV === `development`;

/**
 * Manages the state of a stateful node.
 * Returns the state value and a set of event listeners
 * to be spread on that component.
 */
const useStatefulNode = (props: StatefulNode<any>) => {
  const { hoverProps, isHovered } = useHover({});
  const { pressProps, isPressed } = usePress({});

  let state: NodeState = `unselected`;

  // If there's only one state, don't attach props.
  // Otherwise, we introduce more rerenders.
  if (!props.states || Object.keys(props.states).length === 1) return { state };

  if (props.states?.hover && isHovered && !isPressed) state = `hover`;

  if (props.states?.selected && (props?.selected || isPressed))
    state = `selected`;

  if (props.disabled) state = `disabled`;

  const elementProps = { ...pressProps, ...hoverProps };

  return { state, elementProps };
};

/**
 * Renders a view based on the current stucture context.
 */
function View({
  viewRef,
  ...props
}: ViewType &
  ViewComponentProps & {
    viewRef?: React.RefObject<HTMLDivElement>;
    name: string;
  }) {
  const { structure } = useStructureContext();
  const statefulNodeProps = useStatefulNode(props);

  let inner = null;

  if (props.linearLayout) {
    const linearLayoutProps = structure.linearLayouts[props.linearLayout];

    if (!linearLayoutProps) {
      console.error(
        `lbx/dynamic-web`,
        `Insufficient linear layout data was found for the ${props.linearLayout} view.`,
      );
    } else {
      inner = (
        <LinearLayout {...linearLayoutProps} name={props.linearLayout}>
          {linearLayoutProps.children}
        </LinearLayout>
      );
    }
  }

  const $styles: Styles = {
    ...props.states?.unselected,
    ...props.states?.[statefulNodeProps.state],
  };

  // FIXME: This needs to implement the spec for media rendering.
  const style = props?.media
    ? {
        ...props.style,
        backgroundImage: `url("${props?.media}")`,
        backgroundSize: `contain`,
        backgroundPosition: `center`,
        backgroundRepeat: `no-repeat`,
      }
    : { ...props.style };

  return (
    <NodeStateContext.Provider value={statefulNodeProps.state}>
      <ViewComponent
        {...(isDevelopment && { "data-view": `${String(props.name)}` })}
        {...statefulNodeProps.elementProps}
        // FIXME: Necessary for react-router to work, but conflicts with next line.
        {...props}
        {...prefixWith$(props)}
        $styles={$styles}
        style={style}
        ref={viewRef}
        isSeparator={props.name.toLowerCase().includes(`_seperator`)}
      >
        {inner}
      </ViewComponent>
    </NodeStateContext.Provider>
  );
}

View.defaultProps = {
  viewRef: undefined,
};

/** Maps node types to the default renderer. */
const renderDict: Record<
  keyof DynamicStructureType,
  React.FC<any> | React.ReactNode
> = {
  buttons: Button,
  collectionViews: CollectionView,
  inputs: Input,
  labels: Label,
  linearLayouts: LinearLayout,
  views: View,
};

/**
 * Renders a linear layout based
 */
function LinearLayout(
  props: LinearLayoutType & any & { name: string; children: React.ReactNode },
) {
  const { name, children } = props;
  const { structure, lookup, renderers } = useStructureContext();

  // Check if children is a set of nodes keys.
  const inner = children?.map((child: any) => {
    const type = lookup[child];
    const renderer = renderers[child] || renderDict[type];
    const properties = structure[type][child];

    return (
      <React.Fragment key={`${child}-${Math.floor(Math.random() * 1000 + 1)}`}>
        {typeof renderer === `function` &&
          renderer({ ...properties, name: child } as any)}
      </React.Fragment>
    );
  });

  return (
    <LinearLayoutComponent
      {...(isDevelopment && { "data-linearlayout": `${String(name)}` })}
      {...prefixWith$(props)}
    >
      {inner}
    </LinearLayoutComponent>
  );
}

/**
 * Renders a collection view based on the
 * current structure context.
 */
function CollectionView<T = Record<string, unknown>>(
  props: CollectionViewType & {
    name: string;
    collection: Array<T>;
    loader: React.ReactNode;
    renderer: (p: T) => React.ReactNode;
  },
) {
  const { collection, loader, emptyView, renderer, name } = props;
  const { structure } = useStructureContext();

  // If the collection is loading, display the loader.
  if (loader) {
    return loader;
  }

  let inner: React.ReactNode;

  if (!collection?.length && emptyView) {
    const emptyViewProps = structure.views[emptyView];

    inner = <View name={emptyView} key={0} {...emptyViewProps} />;
  } else {
    inner = collection.map((item) => (
      <React.Fragment
        key={`collectionItem-${Math.floor(Math.random() * 1000 + 1)}`}
      >
        {renderer(item)}
      </React.Fragment>
    ));
  }

  return (
    <CollectionViewComponent
      {...(isDevelopment && { "data-collectionview": `${String(name)}` })}
      {...prefixWith$(props)}
    >
      {inner}
    </CollectionViewComponent>
  );
}

/**
 * Renders a button given the structure context.
 */
function Button({
  ariaLabel,
  sx,
  buttonRef,
  ...props
}: ButtonType &
  ButtonComponentProps & {
    buttonRef?: React.RefObject<HTMLButtonElement>;
  }) {
  const { state, elementProps } = useStatefulNode(props);
  const { structure } = useStructureContext();
  const { linearLayout } = props;
  const { styles, media, label } = merge(
    {},
    props.states?.unselected,
    props.states?.[state],
  );

  let node = null;

  if (label) {
    node = <Label name={label} as="span" />;
  }

  if (linearLayout) {
    const linearLayoutProps = structure.linearLayouts[linearLayout];

    const linearLayoutChildren = linearLayoutProps?.children
      ?.map((name) => {
        if (name === `media`) return media;

        if (name === `label`) return label;

        return null;
      })
      .filter((i) => i !== null);

    node = (
      <LinearLayout name={linearLayout} {...linearLayoutProps}>
        {/* TODO: Fix this type definition */}
        {linearLayoutChildren as any}
      </LinearLayout>
    );
  }

  if (media) {
    const mediaViewProps = structure.views[media];

    node = <View name={media} {...mediaViewProps} />;
  }

  return (
    <NodeStateContext.Provider value={state}>
      <ButtonComponent
        {...(isDevelopment && { "data-button": `${String(props.name)}` })}
        {...prefixWith$(props)}
        {...mergeProps(props, elementProps || {})}
        $styles={styles}
        as={props.as}
        ref={buttonRef}
        sx={sx}
      >
        {ariaLabel && <VisuallyHidden>{ariaLabel}</VisuallyHidden>}
        {node}
      </ButtonComponent>
    </NodeStateContext.Provider>
  );
}

Button.defaultProps = {
  buttonRef: undefined,
};

/**
 * Renders an input given the current structure context.
 */
function Input({
  inputRef,
  ...props
}: InputType &
  InputComponentProps & {
    inputRef?: React.RefObject<HTMLInputElement>;
  }) {
  const {
    placeholder,
    placeholderStyles,
    valueStyles,
    cursorColor,
    ...componentProps
  } = props;
  const placeholderText = useDynamicValue(placeholder);

  return (
    <InputComponent
      {...componentProps}
      {...prefixWith$({ placeholderStyles, valueStyles, cursorColor })}
      placeholder={placeholderText}
      style={{ flex: 1 }}
      ref={inputRef}
    />
  );
}

Input.defaultProps = {
  inputRef: undefined,
};

/**
 * Returns a label.
 */
function Label(
  props: LabelType &
    LabelComponentProps &
    any & {
      name: string;
      children: React.ReactNode;
    },
) {
  const { name, children } = props;
  const {
    structure: { labels },
  } = useStructureContext();
  const value = useDynamicValue(name);
  const {
    alignment: propAlignment,
    frame: propFrame,
    styles: propStyles,
    string: propString,
    ...componentProps
  } = props;

  const alignment = propAlignment || labels?.[name]?.alignment;
  const frame = propFrame || labels?.[name]?.frame;
  const styles = propStyles || labels?.[name]?.styles;
  const string = propString || labels?.[name]?.string;

  return (
    <LabelComponent
      {...(isDevelopment && { "data-label": `${String(name)}` })}
      {...componentProps}
      {...prefixWith$({ alignment, frame, styles, string })}
    >
      {children || value}
    </LabelComponent>
  );
}

/** Returns label text given a name. */
function useDynamicValue(name: string) {
  const SLOT = `\\()`;
  const {
    structure: { labels },
    dynamicValues,
  } = useStructureContext();

  let labelValue = labels[name].string;

  if (dynamicValues[name] && labelValue) {
    // If the original label doesn't have a slot,
    // we want to replace it outright.
    if (!labelValue.includes(SLOT)) {
      labelValue = dynamicValues[name]?.[0] as string;
    } else {
      // Iterate over dynamic values and replace in the string.
      labelValue =
        dynamicValues[name]?.reduce(
          (subject, datum) => subject.replace(SLOT, datum),
          labelValue,
        ) || ``;
    }
  }

  return labelValue as string;
}

/**
 * Returns structure values using external structure
 */
export function getStructureValues<
  D extends Record<string, string> = Record<string, never>,
  S extends DynamicStructureType = DynamicStructureType,
>(dictionary: D, structure: S) {
  const values = Object.keys(dictionary).reduce((accu, key) => {
    const parsedKey = dictionary[key].split(`.`);
    const structureNodeType = parsedKey[0] as DynamicNodeType;

    if (structureNodeType === `buttons`) {
      const searchValue = parsedKey[2] as keyof Styles;

      return {
        [key]:
          structure[structureNodeType]?.[parsedKey[1]]?.states?.unselected
            ?.styles?.[searchValue],
        ...accu,
      };
    }

    if (structureNodeType === `labels`) {
      const searchValue = parsedKey[2] as keyof TextStyles;

      return {
        [key]:
          structure[structureNodeType]?.[parsedKey[1]]?.styles?.[searchValue],
        ...accu,
      };
    }

    // TODO: Add support for more dynamic nodes

    return { [key]: undefined, ...accu };
  }, {});

  return values as Record<keyof D, string>;
}

/**
 * Returns structure values using external structure
 */
export function updateStructureValues<
  D extends Record<string, string[]>,
  S extends DynamicStructureType = DynamicStructureType,
>(dictionary: D, structure: S, values: Partial<Record<keyof D, string>>) {
  const construct = structure;

  Object.keys(dictionary).forEach((key) => {
    if (values[key]) {
      const newValue = String(values[key]);
      dictionary[key].forEach((structureKey) => {
        const parsedKey = structureKey.split(`.`);
        const structureNodeType = parsedKey[0] as DynamicNodeType;

        // Modifying button, not label
        if (structureNodeType === `buttons` && parsedKey[2] !== `label`) {
          const isValidColor = chroma.valid(newValue);
          const [h, s, l] = isValidColor ? chroma(newValue).hsl() : [0, 0, 0];
          construct[structureNodeType] = merge(structure[structureNodeType], {
            [parsedKey[1]]: {
              states: {
                unselected: {
                  styles: {
                    [parsedKey[2]]: values[key],
                    stroke: {
                      color: values[key],
                    },
                  },
                },
                ...(parsedKey[2] === `fillColor` &&
                  isValidColor && {
                    selected: {
                      styles: {
                        [parsedKey[2]]: chroma(h, s, l - 0.1, `hsl`).hex(),
                        stroke: {
                          color: chroma(h, s, l - 0.1, `hsl`).hex(),
                        },
                      },
                    },
                    hover: {
                      styles: {
                        [parsedKey[2]]: chroma(h, s, l + 0.1, `hsl`).hex(),
                        stroke: {
                          color: chroma(h, s, l + 0.1, `hsl`).hex(),
                        },
                      },
                    },
                  }),
              },
            },
          });
        }

        // Modifiy button's label
        if (structureNodeType === `buttons` && parsedKey[2] === `label`) {
          const buttonStates =
            structure[structureNodeType]?.[parsedKey[1]]?.states || {};

          Object.keys(buttonStates).forEach((state) => {
            const stateLabelKey = buttonStates?.[state as NodeState]?.label;

            if (
              state === `unselected` ||
              state === `selected` ||
              state === `hover`
            ) {
              construct.labels = merge(
                structure.labels,
                // Only add if the button state label is defined
                stateLabelKey && {
                  [stateLabelKey]: {
                    styles: {
                      [parsedKey[3]]: values[key],
                    },
                  },
                },
              );
            }

            // TODO: Add more state values automatically using color functions
          });
        }
      });
    }
  });

  return construct;
}

/**
 * Renders a dynamic structure.
 */
function DynamicFactory<
  T extends DynamicStructureType = typeof defaultStructure,
>({ entry = ``, ...props }: DynamicFactoryProps<T>) {
  const ctx = React.useContext(StructureContext) as StructureContextValue<T>;

  const { structure, dynamicValues, renderers, ...merged } = merge(
    {},
    ctx,
    props,
  );

  if (!Object.keys(structure).length || !entry) {
    console.error(
      `lbx/dynamic-web`,
      `Invalid properties provided for the dynamic factory.`,
    );

    return null;
  }

  const entryViewAttributes = structure.views[`${String(entry)}`];

  const lookup: StructureContextValue<T>["lookup"] = { ...merged.lookup };

  Object.entries(structure).forEach(([parent, child]) => {
    Object.keys(child).forEach((key) => {
      lookup[key] = parent as keyof DynamicStructureType;
    });
  });

  const renderer = (renderers[`${String(entry)}`] as any) || (View as any);

  return (
    <StructureContext.Provider
      // eslint-disable-next-line
      value={{
        structure,
        dynamicValues,
        renderers,
        lookup,
      }}
    >
      {typeof renderer === `function` &&
        // TODO: Fix type definitions
        renderer({ ...entryViewAttributes, name: `${String(entry)}` })}
    </StructureContext.Provider>
  );
}

export default DynamicFactory;
export {
  Button,
  CollectionView,
  Input,
  Label,
  LinearLayout,
  View,
  prefixWith$,
};

export { default as defaultDynamicFactoryStructure } from "./structure";
export * from "./types";
